/**
 * <s-back-to-top> — floating "back to top" control.
 *
 * Reveals after the shopper scrolls past roughly one screen and hides near the top.
 * The ring around the arrow fills as the page scrolls (CSS scroll-driven where
 * supported; set from JS otherwise). Click glides to the top (instant under
 * reduced motion) and moves focus to the main content for keyboard users.
 */
class SBackToTop extends HTMLElement {
  #ac;
  #raf = 0;

  connectedCallback() {
    this.button = this.querySelector('button');
    this.ring = this.querySelector('[data-ring]');
    if (!this.button) return;

    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };
    this.cssProgress = CSS.supports('animation-timeline: scroll()');

    window.addEventListener('scroll', () => this.#schedule(), { ...opts, passive: true });
    window.addEventListener('resize', () => this.#schedule(), { ...opts, passive: true });
    this.button.addEventListener('click', () => this.#toTop(), opts);
    this.#update();
  }

  disconnectedCallback() {
    this.#ac?.abort();
    cancelAnimationFrame(this.#raf);
  }

  #schedule() {
    cancelAnimationFrame(this.#raf);
    this.#raf = requestAnimationFrame(() => this.#update());
  }

  #update() {
    const y = window.scrollY;
    const visible = y > window.innerHeight * 0.9;
    this.toggleAttribute('data-visible', visible);
    this.button.tabIndex = visible ? 0 : -1;

    if (!this.cssProgress && this.ring) {
      const max = document.documentElement.scrollHeight - window.innerHeight;
      const progress = max > 0 ? Math.min(1, y / max) : 0;
      this.ring.style.strokeDashoffset = String(100 - progress * 100);
    }
  }

  #toTop() {
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    window.scrollTo({ top: 0, behavior: reduce ? 'auto' : 'smooth' });
    document.getElementById('main-content')?.focus({ preventScroll: true });
  }
}

if (!customElements.get('s-back-to-top')) {
  customElements.define('s-back-to-top', SBackToTop);
}
